<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'update:show'])

const users = ref([])
const leagues = ref([])
const clientLeagues = ref([])
const selectedEmail = ref(null)
const selectedClientLeague = ref(null)
const loading = ref(false)
const saving = ref(false)
const activeTab = ref('all') // 'all' or 'search'
const searchQuery = ref('')
const selectedLeaguesSearchQuery = ref('') // New search query for selected leagues

const accessTypes = [
  { value: 'all', label: 'All Leagues' },
  { value: 'all_except', label: 'All Except' },
  { value: 'specific', label: 'Specific Leagues' }
]

const hiddenFieldsOptions = [
  { value: 'user_data', label: 'User Data' },
  { value: 'user_id', label: 'User ID' }
]

const selectedLeagues = computed(() => {
  if (!selectedClientLeague.value || !selectedClientLeague.value.league_slugs) {
    return []
  }
  return selectedClientLeague.value.league_slugs
})

const disabledLeagues = computed(() => {
  if (!selectedClientLeague.value) {
    return []
  }
  // Handle null or undefined disabled_leagues
  return selectedClientLeague.value.disabled_leagues || []
})

const filteredLeagues = computed(() => {
  if (!leagues.value) return []
  
  let filtered = leagues.value
  
  // Filter by search query
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(league => 
      league.league_name?.toLowerCase().includes(query) ||
      league.slug?.toLowerCase().includes(query)
    )
  }
  
  return filtered
})

const selectedLeaguesList = computed(() => {
  if (!selectedLeagues.value || !leagues.value) return []
  
  let filtered = leagues.value.filter(league => 
    selectedLeagues.value.includes(league.slug)
  )
  
  // Filter by search query for selected leagues
  if (selectedLeaguesSearchQuery.value.trim()) {
    const query = selectedLeaguesSearchQuery.value.toLowerCase()
    filtered = filtered.filter(league => 
      league.league_name?.toLowerCase().includes(query) ||
      league.slug?.toLowerCase().includes(query)
    )
  }
  
  return filtered
})

onMounted(async () => {
  await loadData()
})

watch(() => props.show, (newVal) => {
  if (newVal) {
    loadData()
  }
})

async function loadData() {
  loading.value = true
  try {
    const [usersRes, leaguesRes, clientLeaguesRes] = await Promise.all([
      axios.get('/api/client-leagues-manager/users'),
      axios.get('/api/client-leagues-manager/leagues'),
      axios.get('/api/client-leagues-manager')
    ])
    
    users.value = usersRes.data
    leagues.value = leaguesRes.data
    clientLeagues.value = clientLeaguesRes.data
    
    // Set first user as selected if available
    if (users.value.length > 0 && !selectedEmail.value) {
      selectUser(users.value[0].email)
    }
  } catch (error) {
    console.error('Error loading data:', error)
    alert('Failed to load data. Please try again.')
  } finally {
    loading.value = false
  }
}

function selectUser(email) {
  selectedEmail.value = email
  const existing = clientLeagues.value.find(cl => cl.user_email === email)
  
  if (existing) {
    // Ensure disabled_leagues is initialized as array if null
    selectedClientLeague.value = {
      ...existing,
      disabled_leagues: existing.disabled_leagues || []
    }
  } else {
    selectedClientLeague.value = {
      user_email: email,
      access_type: 'all_except',
      league_slugs: [],
      disabled_leagues: [],
      hidden_fields: [],
      can_see_test_leagues: false
    }
    // Auto-save when creating new configuration
    autoSave()
  }
}

async function autoSave() {
  if (!selectedClientLeague.value || !selectedEmail.value) return
  
  saving.value = true
  try {
    // Ensure disabled_leagues is always an array (not null)
    const dataToSave = {
      ...selectedClientLeague.value,
      disabled_leagues: selectedClientLeague.value.disabled_leagues || []
    }
    
    await axios.post('/api/client-leagues-manager', dataToSave)
    
    // Update the clientLeagues array to reflect the change
    const index = clientLeagues.value.findIndex(cl => cl.user_email === selectedEmail.value)
    if (index > -1) {
      clientLeagues.value[index] = { ...dataToSave }
    } else {
      clientLeagues.value.push({ ...dataToSave })
    }
  } catch (error) {
    console.error('Error auto-saving configuration:', error)
    // Don't show alert for auto-save failures, just log
  } finally {
    saving.value = false
  }
}

function addLeague(leagueSlug) {
  if (!selectedClientLeague.value) return
  
  const slugs = selectedClientLeague.value.league_slugs || []
  if (!slugs.includes(leagueSlug)) {
    slugs.push(leagueSlug)
    selectedClientLeague.value.league_slugs = slugs
    
    // Remove from disabled if it was disabled
    const disabled = selectedClientLeague.value.disabled_leagues || []
    const disabledIndex = disabled.indexOf(leagueSlug)
    if (disabledIndex > -1) {
      disabled.splice(disabledIndex, 1)
      selectedClientLeague.value.disabled_leagues = disabled
    }
    
    autoSave()
  }
}

function addAllLeagues() {
  if (!selectedClientLeague.value || !leagues.value) return
  
  const allSlugs = leagues.value.map(league => league.slug).filter(Boolean)
  selectedClientLeague.value.league_slugs = [...new Set(allSlugs)] // Remove duplicates
  autoSave()
}

function removeLeague(leagueSlug) {
  if (!selectedClientLeague.value) return
  
  const slugs = selectedClientLeague.value.league_slugs || []
  const index = slugs.indexOf(leagueSlug)
  if (index > -1) {
    slugs.splice(index, 1)
    selectedClientLeague.value.league_slugs = slugs
    
    // Also remove from disabled if it was there
    const disabled = selectedClientLeague.value.disabled_leagues || []
    const disabledIndex = disabled.indexOf(leagueSlug)
    if (disabledIndex > -1) {
      disabled.splice(disabledIndex, 1)
      selectedClientLeague.value.disabled_leagues = disabled
    }
    
    autoSave()
  }
}

function toggleLeagueDisabled(leagueSlug) {
  if (!selectedClientLeague.value) return
  
  // Ensure disabled_leagues is initialized as array
  if (!selectedClientLeague.value.disabled_leagues) {
    selectedClientLeague.value.disabled_leagues = []
  }
  
  const disabled = selectedClientLeague.value.disabled_leagues
  const index = disabled.indexOf(leagueSlug)
  
  if (index > -1) {
    // Enable league (remove from disabled)
    disabled.splice(index, 1)
  } else {
    // Disable league (add to disabled)
    disabled.push(leagueSlug)
  }
  
  selectedClientLeague.value.disabled_leagues = disabled
  autoSave()
}

function isLeagueDisabled(leagueSlug) {
  return disabledLeagues.value.includes(leagueSlug)
}

function isLeagueInSelected(leagueSlug) {
  return selectedLeagues.value.includes(leagueSlug)
}

function switchTab(tab) {
  activeTab.value = tab
  if (tab === 'all') {
    searchQuery.value = ''
  }
}

async function saveConfiguration() {
  if (!selectedClientLeague.value) return
  
  saving.value = true
  try {
    await axios.post('/api/client-leagues-manager', selectedClientLeague.value)
    
    // Reload data
    await loadData()
    
    alert('Configuration saved successfully!')
  } catch (error) {
    console.error('Error saving configuration:', error)
    alert('Failed to save configuration. Please try again.')
  } finally {
    saving.value = false
  }
}

async function deleteConfiguration() {
  if (!selectedEmail.value || !confirm('Are you sure you want to delete this configuration?')) {
    return
  }
  
  try {
    await axios.delete(`/api/client-leagues-manager/${encodeURIComponent(selectedEmail.value)}`)
    
    // Reload data
    await loadData()
    
    // Select first user
    if (users.value.length > 0) {
      selectUser(users.value[0].email)
    } else {
      selectedEmail.value = null
      selectedClientLeague.value = null
    }
    
    alert('Configuration deleted successfully!')
  } catch (error) {
    console.error('Error deleting configuration:', error)
    alert('Failed to delete configuration. Please try again.')
  }
}

function close() {
  emit('update:show', false)
  emit('close')
}
</script>

<template>
  <div 
    v-if="show"
    class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
    @click="close"
  >
    <div 
      class="relative  z-10 bg-white rounded-lg shadow-2xl w-full  h-[90vh] flex flex-col"
      @click.stop
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Manage Clients Leagues</h3>
            <p class="text-xs text-gray-500 mt-1">Configure league access for each client</p>
          </div>
          <div v-if="saving" class="flex items-center gap-2 text-sm text-pink-600">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Saving...</span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <button
            v-if="clientLeagues.find(cl => cl.user_email === selectedEmail)"
            @click="deleteConfiguration"
            class="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete
          </button>
          <button 
            class="text-gray-500 hover:text-gray-700 p-2"
            @click="close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden flex min-h-0">
        <!-- Left Sidebar - Users List -->
        <div class="w-1/4 border-r border-gray-200 overflow-y-auto bg-gray-50">
          <div class="p-4">
            <h4 class="text-sm font-semibold text-gray-700 mb-3">Users</h4>
            <div v-if="loading" class="text-center py-4 text-gray-500">
              Loading...
            </div>
            <div v-else class="space-y-1">
              <button
                v-for="user in users"
                :key="user.id"
                @click="selectUser(user.email)"
                class="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 transition-colors"
                :class="{
                  'bg-pink-50 text-pink-700 font-medium': selectedEmail === user.email,
                  'text-gray-700': selectedEmail !== user.email
                }"
              >
                <div class="font-medium">{{ user.email }}</div>
                <div class="text-xs text-gray-500">{{ user.name || 'No name' }}</div>
              </button>
            </div>
          </div>
        </div>

        <!-- Right Side - Configuration -->
        <div class="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div v-if="!selectedEmail" class="flex-1 flex items-center justify-center text-gray-500">
            <div class="text-center">
              <i class="fas fa-user text-4xl mb-4"></i>
              <p>Select a user from the left to configure their league access</p>
            </div>
          </div>

          <div v-else-if="loading" class="flex-1 flex items-center justify-center text-gray-500">
            Loading configuration...
          </div>

          <div v-else class="flex-1 flex flex-col min-h-0">
            <!-- Two Column Layout - Leagues -->
            <div class="flex-1 flex gap-6 min-h-0 p-4 bg-gray-50">
              <!-- Left Column - Available Leagues -->
              <div class="basis-1/4 flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                <!-- Tabs -->
                <div class="flex border-b border-gray-200 mb-4">
                  <button 
                    @click="switchTab('all')"
                    class="flex-1 py-2 px-4 text-center font-medium text-sm"
                    :class="activeTab === 'all' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-500 hover:text-gray-700'"
                  >
                    All Leagues
                  </button>
                  <button 
                    @click="switchTab('search')"
                    class="flex-1 py-2 px-4 text-center font-medium text-sm"
                    :class="activeTab === 'search' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-500 hover:text-gray-700'"
                  >
                    Search Leagues
                  </button>
                </div>

                <!-- Search input (only show in search tab) -->
                <div v-if="activeTab === 'search'" class="mb-4">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-search text-pink-500 mr-2"></i>
                    <span class="font-semibold text-gray-700">Search Leagues</span>
                  </div>
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search leagues..."
                    class="w-full px-3 py-2 border rounded focus:outline-none focus:border-pink-500 text-sm"
                  />
                </div>

                <!-- All Leagues Header (only show in all tab) -->
                <div v-else class="flex items-center justify-between mb-4">
                  <div class="flex items-center">
                    <i class="fas fa-list text-pink-500 mr-2"></i>
                    <span class="font-semibold text-gray-700">All Leagues</span>
                  </div>
                  <button
                    @click="addAllLeagues"
                    class="px-3 py-1.5 text-xs bg-pink-500 text-white rounded hover:bg-pink-600 flex items-center gap-1"
                    title="Add all leagues"
                  >
                    <i class="fas fa-plus"></i>
                    <span>Add All</span>
                  </button>
                </div>

                <!-- Leagues List -->
                <ul 
                  v-if="filteredLeagues.length" 
                  class="space-y-2 flex-1 overflow-auto min-h-0"
                >
                  <li 
                    v-for="league in filteredLeagues" 
                    :key="league.id" 
                    class="flex items-center justify-between bg-gray-50 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
                    :class="{ 'ring-1 ring-pink-200 bg-pink-50': isLeagueInSelected(league.slug) }"
                  >
                    <div class="flex items-center space-x-2 flex-1 min-w-0">
                      <input 
                        type="checkbox" 
                        class="form-checkbox text-pink-500 mr-2 flex-shrink-0"
                        :checked="isLeagueInSelected(league.slug)"
                        @click.stop="isLeagueInSelected(league.slug) ? removeLeague(league.slug) : addLeague(league.slug)"
                      />
                      <i class="fas fa-trophy text-gray-400 flex-shrink-0"></i>
                      <div class="flex-1 min-w-0">
                        <div class="text-sm text-gray-700 truncate">{{ league.league_name }}</div>
                        <div class="text-xs text-gray-400">id: {{ league.slug }}</div>
                      </div>
                    </div>
                    <template v-if="isLeagueInSelected(league.slug)">
                      <span class="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-semibold flex-shrink-0">Added</span>
                    </template>
                    <template v-else>
                      <button
                        @click.stop="addLeague(league.slug)"
                        class="text-sm px-2 py-1 rounded flex items-center space-x-1 min-w-[60px] justify-center bg-pink-100 text-pink-700 hover:bg-pink-200 flex-shrink-0"
                      >
                        <span>Add</span>
                      </button>
                    </template>
                  </li>
                </ul>
                <div v-else-if="!loading" class="text-gray-400 italic flex-1 flex items-center justify-center">
                  {{ activeTab === 'search' ? 'No leagues found.' : 'No leagues available.' }}
                </div>
              </div>

              <!-- Right Column - Selected Leagues -->
              <div class="basis-3/4 flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center">
                    <i class="fas fa-users text-pink-500 mr-2"></i>
                    <span class="font-semibold text-gray-700">Selected Leagues</span>
                  </div>
                </div>
                
                <!-- Search input for selected leagues -->
                <div class="mb-4">
                  <div class="flex items-center mb-2">
                    <i class="fas fa-search text-pink-500 mr-2"></i>
                    <span class="font-semibold text-gray-700 text-sm">Search Selected Leagues</span>
                  </div>
                  <input
                    v-model="selectedLeaguesSearchQuery"
                    type="text"
                    placeholder="Search selected leagues..."
                    class="w-full px-3 py-2 border rounded focus:outline-none focus:border-pink-500 text-sm"
                  />
                </div>
                
                <ul v-if="selectedLeaguesList.length" class="space-y-2 flex-1 overflow-auto min-h-0">
                  <li 
                    v-for="league in selectedLeaguesList" 
                    :key="league.id" 
                    class="flex items-center justify-between bg-gray-50 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
                    :class="{ 'opacity-50': isLeagueDisabled(league.slug) }"
                  >
                    <!-- Toggle Switch -->
                    <label class="relative inline-flex items-center cursor-pointer mr-3 flex-shrink-0">
                      <input
                        type="checkbox"
                        :checked="!isLeagueDisabled(league.slug)"
                        @change="toggleLeagueDisabled(league.slug)"
                        class="sr-only peer"
                      />
                      <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-pink-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                    </label>
                    
                    <div class="flex items-center space-x-2 flex-1">
                      <i class="fas fa-trophy text-gray-400"></i>
                      <span class="text-sm text-gray-700" :class="{ 'line-through text-gray-400': isLeagueDisabled(league.slug) }">{{ league.league_name }}</span>
                      <span class="text-xs text-gray-400">({{ league.slug }})</span>
                    </div>
                    <button
                      @click.stop="removeLeague(league.slug)"
                      class="text-sm px-2 py-1 rounded flex items-center justify-center transition-all duration-150 group bg-transparent"
                      title="Remove league"
                    >
                      <i class="fas fa-trash-alt text-red-400 opacity-30 grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:text-red-600 transition-all duration-150"></i>
                    </button>
                  </li>
                </ul>
                <div v-else class="text-gray-400 italic flex-1 flex items-center justify-center">
                  {{ selectedLeaguesSearchQuery.trim() ? 'No leagues found.' : 'No leagues selected.' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any custom styles if needed */
</style>
